type UnknownRecord = Record<string, unknown>

const TRANSIENT_INVOICE_SAVE_MESSAGE =
  'نتیجه ذخیره صورتحساب به‌دلیل اختلال موقت سرویس مشخص نیست. برای جلوگیری از ثبت تکراری، ابتدا فهرست صورتحساب‌ها را تازه‌سازی و نتیجه را بررسی کنید؛ فقط اگر صورتحساب ثبت نشده بود، دوباره تلاش کنید.'

const technicalExceptionPatterns = [
  /\bexception\b/i,
  /\binner exception\b/i,
  /\bstack trace\b/i,
  /\bsqlazureexecutionstrategy\b/i,
  /\bobject reference not set to an instance of an object\b/i,
  /\bthe underlying provider failed on open\b/i,
  /\ban error occurred while updating the entries\b/i,
  /(?:^|\r?\n)\s*at\s+[a-z0-9_.<>]+\s*\(/i,
]

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const readStrings = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.flatMap(readStrings)
  if (isRecord(value)) return Object.values(value).flatMap(readStrings)
  return typeof value === 'string' && value.trim() ? [value.trim()] : []
}

const isEfTransientFailure = (message: string) => {
  const normalized = message.toLowerCase()
  return (
    normalized.includes('likely due to a transient failure') &&
    normalized.includes('sqlazureexecutionstrategy')
  )
}

const isTechnicalExceptionMessage = (message: string) =>
  technicalExceptionPatterns.some((pattern) => pattern.test(message))

const isInvoiceSaveFallback = (fallback: string) => /(?:ذخیره|صدور) صورتحساب/.test(fallback)

const isTransportFailure = (
  error: UnknownRecord | undefined,
  response: UnknownRecord | undefined
) => {
  const status = Number(response?.status)
  if (Number.isFinite(status) && (status >= 500 || status === 408 || status === 429)) return true
  if (response) return false

  const code = typeof error?.code === 'string' ? error.code.toUpperCase() : ''
  const message = typeof error?.message === 'string' ? error.message.toLowerCase() : ''
  return (
    ['ECONNABORTED', 'ERR_NETWORK', 'ETIMEDOUT'].includes(code) ||
    message.includes('network error') ||
    message.includes('timeout') ||
    message.includes('failed to fetch')
  )
}

const normalizeMessage = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined

  const message = value.trim()
  if (!message) return undefined

  const normalized = message
    .toLowerCase()
    .replace(/[.!]+$/g, '')
    .trim()
  const genericMessages = [
    'an error has occurred',
    'an error has been occurred',
    'an error has been occured',
    'an error has been accured',
    'request failed with status code 400',
    'request failed with status code 500',
    'network error',
  ]

  return genericMessages.includes(normalized) || isTechnicalExceptionMessage(message)
    ? undefined
    : message
}

const readErrorCollection = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap(readErrorCollection)
  }

  if (isRecord(value)) {
    return Object.values(value).flatMap(readErrorCollection)
  }

  const message = normalizeMessage(value)
  return message ? [message] : []
}

const readResponseMessages = (data: unknown): string[] => {
  const directMessage = normalizeMessage(data)
  if (directMessage) return [directMessage]
  if (!isRecord(data)) return []

  const payload = isRecord(data.payload) ? data.payload : undefined
  const messages = [
    data.ExceptionMessage,
    data.exceptionMessage,
    data.Message,
    data.message,
    data.detail,
    data.title,
    payload?.message,
    payload?.Message,
  ]
    .map(normalizeMessage)
    .filter((message): message is string => !!message)

  const validationMessages = [
    data.errors,
    data.ModelState,
    data.modelState,
    payload?.errors,
  ].flatMap(readErrorCollection)

  return [...messages, ...validationMessages]
}

// Kept local: invoiceExchangeAnnouncement.ts imports this module.
const EXCHANGE_ANNOUNCEMENT_CODE_PREFIX = 'EXCHANGE_ANNOUNCEMENT_'

// The invoice was sent but the tax system's answer was lost: edit, reset, restore, date change and
// resend are refused until the result is settled (by inquiry, or by support review).
const SEND_RESULT_UNKNOWN_CODE = 'INVOICE_SEND_RESULT_UNKNOWN'
const SEND_RESULT_UNKNOWN_MESSAGE =
  'این صورتحساب به سامانه مؤدیان ارسال شده و پاسخ آن هنوز دریافت نشده است. تا زمانی که نتیجهٔ آن مشخص نشود، ویرایش، تغییر تاریخ و ارسال دوباره امکان‌پذیر نیست.'

// A refusal the server marks with a code is definite: nothing was saved, so it must never read as
// an unknown save result (even as 503), and its text is shown once although the server repeats it
// in Message and message. Returns undefined for every other response.
// - EXCHANGE_ANNOUNCEMENT_*: pattern 11 correction refused by the exchange announcement gate.
// - INVOICE_SEND_RESULT_UNKNOWN: the invoice holds an unresolved send.
const readCodedRefusal = (data: unknown, fallback: string): string | undefined => {
  if (!isRecord(data)) return undefined

  const payload = isRecord(data.payload) ? data.payload : undefined
  const code = [data.code, data.Code, payload?.code, payload?.Code]
    .find(
      (candidate): candidate is string => typeof candidate === 'string' && candidate.trim() !== ''
    )
    ?.trim()
    .toUpperCase()
  const isSendResultUnknown = code === SEND_RESULT_UNKNOWN_CODE
  if (!code || (!isSendResultUnknown && !code.startsWith(EXCHANGE_ANNOUNCEMENT_CODE_PREFIX))) {
    return undefined
  }

  const message = [data.message, data.Message, payload?.message, payload?.Message]
    .map(normalizeMessage)
    .find((candidate): candidate is string => !!candidate)
  // The caller's fallback usually says "try again", which is wrong while the send is unresolved.
  return message || (isSendResultUnknown ? SEND_RESULT_UNKNOWN_MESSAGE : fallback)
}

const getInvoiceErrorMessage = (error: unknown, fallback: string): string => {
  const errorRecord = isRecord(error) ? error : undefined
  const responseValue = errorRecord?.response
  const response = isRecord(responseValue) ? responseValue : undefined

  const codedRefusal = readCodedRefusal(response?.data, fallback)
  if (codedRefusal) return codedRefusal

  const rawMessages = [...readStrings(response?.data), ...readStrings(errorRecord?.message)]

  if (
    isInvoiceSaveFallback(fallback) &&
    (rawMessages.some(isEfTransientFailure) || isTransportFailure(errorRecord, response))
  ) {
    return TRANSIENT_INVOICE_SAVE_MESSAGE
  }

  const status = Number(response?.status)
  if (Number.isFinite(status) && status >= 500) return fallback

  const messages = readResponseMessages(response?.data)
  const uniqueMessages = Array.from(new Set(messages))

  if (uniqueMessages.length > 0) {
    return uniqueMessages.join('\n')
  }

  return normalizeMessage(errorRecord?.message) || fallback
}

const getInvoiceErrorAppLogId = (error: unknown): number | undefined => {
  const errorRecord = isRecord(error) ? error : undefined
  const responseValue = errorRecord?.response
  const response = isRecord(responseValue) ? responseValue : undefined
  const dataValue = response?.data
  const data = isRecord(dataValue) ? dataValue : undefined
  const payloadValue = data?.payload
  const payload = isRecord(payloadValue) ? payloadValue : undefined
  const candidates = [
    data?.appLogId,
    data?.AppLogId,
    data?.logId,
    payload?.appLogId,
    payload?.AppLogId,
  ]

  for (const candidate of candidates) {
    const parsed = Number(candidate)
    if (Number.isSafeInteger(parsed) && parsed > 0) return parsed
  }

  return undefined
}

export {getInvoiceErrorAppLogId, getInvoiceErrorMessage, TRANSIENT_INVOICE_SAVE_MESSAGE}
